import { Trans, useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import type { PremiseConfig } from "@shared/types/foyer";
import type {
  OfferActionKind,
  OfferOverrideField,
  OfferOverridePatch,
  OfferReach,
  OfferScope,
  ResolvedRoomOffer,
  RoomOffer,
  RoomOfferOverride,
} from "@shared/types/roomOffer";
import {
  hasImportedLines,
  offerStatusAt,
  offerStatusReason,
  type OfferStatus,
} from "@shared/roomOffers";
import { resolveRoomOffers } from "@shared/roomOfferLayers";
import { readLocalized } from "@shared/i18n";
import { usePermission, type PlaceRef } from "../auth/access";
import {
  foyerApi,
  offerScopeKey,
  type OfferScopeRef,
  type RoomOfferBody,
} from "../http/foyer";
import { PageContainer, PageHeader } from "../components/PageHeader";
import LivePreview from "../components/template/LivePreview";
import OfferDialog from "../components/offers/OfferDialog";
import OfferOverrideDialog from "../components/offers/OfferOverrideDialog";
import DemoPaymentsBanner from "../components/offers/DemoPaymentsBanner";
import PmsOffersImport from "../components/link/PmsOffersImport";
import OfferIcon from "../components/offers/OfferIcon";
import {
  offerKindLabel,
  offerProblemsOf,
  offerStatusLabel,
  type OfferProblem,
} from "../components/offers/offerCopy";
import { tokens } from "../theme";
import { describeError, useToast } from "../components/toast";
import type { Translate } from "../i18n/translate";

/**
 * The Offers catalogue — one list and one dialog (`POS-5`).
 *
 * Where it hangs was settled by `docs/room-offers.md` §9 and is not reopened
 * here: a section of `FRONTLAYER_SECTIONS` at `/frontlayer/offers`, not a
 * `ProductKey`. Offers renders into a template's own screens, is authored
 * beside the template's content and draws from the same media library — a
 * product whose every route 404s without another product is a section wearing a
 * product's clothes.
 *
 * ## What the list shows, and why it shows the ones that are off
 *
 * Everything authored. The catalogue route deliberately answers with the
 * disabled rows and the out-of-season ones too, because **a row IS its draft**
 * — a list that hid what it could not currently show would leave an operator no
 * way to reach the spa evening they are preparing for December. The television
 * reads a different URL entirely (POS-3's), so "what is authored" and "what is
 * on screen" are never confused for one another.
 *
 * Status is **derived** by `offerStatusAt()` and never stored. A stored one
 * drifts from the window it was computed from within a week, and then the list
 * claims "Live now" while the editor shows a schedule that ended in March.
 *
 * ## Inherited rows are read-only here
 *
 * A hotel sees its own offers plus its chain's plus Nenda's global starter
 * catalogue, each tagged with a derived `scope`. Reads inherit; writes never
 * do. The row menu is hidden on an inherited row rather than offered and then
 * refused with a 404 — the server would refuse it, and a control that always
 * fails is worse than one that is not there.
 */

/** How long the derived status may be stale before the list recomputes it. */
const STATUS_TICK_MS = 60_000;

export interface OffersPageProps {
  /**
   * Rows as THIS layer sees them, already through `resolveRoomOffers()`.
   *
   * Resolved rather than raw (`POS-14`): the page draws provenance and
   * per-location overrides, and deriving either from the two id fields here
   * would be a second implementation of the rule the television runs.
   */
  offers: ResolvedRoomOffer[];
  /** Which layer this page writes to — decides which rows are editable. */
  scope: OfferScopeRef;
  loading?: boolean;
  canEdit: boolean;
  /** Rendered above the list. The premise branch passes a `LivePreview`. */
  preview?: React.ReactNode;
  /** The hotel's PMS import (`PmsOffersImport`), above the preview. */
  pmsImport?: React.ReactNode;
  /** Named when authoring for a whole customer, so the reach is never a surprise. */
  chainName?: string;
  chainLocationCount?: number;
  /**
   * The QUERIED blast radius — properties and rooms this page's saves reach.
   *
   * Undefined only while the one call is in flight; the banner falls back to
   * the directory's location count until it lands, and never invents a room
   * number. See `companyOfferReach()`.
   */
  chainReach?: OfferReach;
  /** Re-decide an inherited row for this location. Premise scope only. */
  onOverride?: (offer: ResolvedRoomOffer) => void;
  /** Hide, or un-hide, an inherited row at this location. */
  onSuppress?: (offer: ResolvedRoomOffer, suppressed: boolean) => void;
  /**
   * A refusal that happened with no dialog open, said out loud.
   *
   * The list's own switches write to the server, and a failure that only
   * flipped the switch back would leave an operator believing they had hidden
   * a chain campaign that is still on every television in the building.
   */
  notice?: string | null;
  onDismissNotice?: () => void;
  defaultLocale: string;
  /**
   * The premise's own IANA zone.
   *
   * NOT optional decoration: `offerStatusAt` falls back to UTC, and a Stockholm
   * hotel whose offer runs 07:00–22:00 would then read "Scheduled" for the two
   * hours before local midnight while the television is still showing it. A
   * status column that disagrees with the screen is worse than none.
   */
  timezone?: string;
  onCreate: () => void;
  onEdit: (offer: RoomOffer) => void;
  onDelete: (offer: RoomOffer) => void;
  onToggle: (offer: RoomOffer, enabled: boolean) => void;
}

/** Is this row addressable at the layer the page is pointed at? */
export function isOwnRow(offer: RoomOffer, scope: OfferScopeRef): boolean {
  return scope.kind === "premise"
    ? offer.scope === "premise"
    : offer.scope === "company";
}

/** One line saying what a guest actually gets, without opening the row. */
function kindSummary(t: Translate, offer: RoomOffer): string {
  const action = offer.action;
  const price =
    "price" in action && action.price
      ? `${action.price.amount} ${action.price.currency}`
      : null;
  if (action.kind === "promotion")
    return t("offers:offersPage.kindSummary.nothingToPress");
  if (action.kind === "order") {
    const where =
      action.fulfil === "deliver"
        ? t("offers:offersPage.kindSummary.deliver")
        : t("offers:offersPage.kindSummary.collect");
    return [price, where].filter(Boolean).join(" · ");
  }
  const timetable =
    action.slots.mode === "asap"
      ? t("offers:offersPage.kindSummary.asap")
      : t("offers:offersPage.kindSummary.slots", {
          start: action.slots.startTime,
          end: action.slots.endTime,
          capacity: action.slots.capacity,
        });
  return [price, timetable].filter(Boolean).join(" · ");
}

/**
 * Where an inherited row was authored, as a provenance rather than a scope
 * name: the chip answers "who decides this?".
 */
const ORIGIN_KEY = {
  premise: "offersPage.origin.premise",
  company: "offersPage.origin.company",
  global: "offersPage.origin.global",
} as const satisfies Record<OfferScope, string>;

/** What each re-decided field is called in the "Overridden here" chip. */
const OVERRIDE_FIELD_KEY = {
  price: "offersPage.overrideField.price",
  availability: "offersPage.overrideField.availability",
  imageUrl: "offersPage.overrideField.imageUrl",
} as const satisfies Record<OfferOverrideField, string>;

/**
 * The blast radius, in words, from the QUERIED counts (`POS-14`).
 *
 * Real numbers or none: while the query is in flight the banner falls back to
 * the directory's location count and says nothing about rooms, because an
 * invented room number is worse than a missing one — this sentence is the last
 * thing an operator reads before publishing to every building the chain owns,
 * and the mistake is only visible on a television somewhere else.
 */
function blastRadius(
  t: Translate,
  reach?: OfferReach,
  fallbackLocations?: number
): string {
  const locations = reach ? reach.locations : (fallbackLocations ?? 0);
  const places = t("offers:offersPage.reach.locations", { count: locations });
  if (!reach) return places;
  return t("offers:offersPage.reach.both", {
    locations: places,
    rooms: t("offers:offersPage.reach.rooms", { count: reach.rooms }),
  });
}

/**
 * The selector above the list (Sigge, 2026-10-28): a bare list of every offer
 * a hotel can see, its own plus its chain's, Nenda's and its PMS's, was
 * unreadable past a dozen rows. Kind tabs, a search and two filters narrow it.
 */
export type OfferKindTab = "all" | OfferActionKind;
export type OfferSource = "own" | "chain" | "nenda" | "pms";
export interface OfferFilter {
  kind: OfferKindTab;
  search: string;
  status: OfferStatus | "any";
  source: OfferSource | "any";
}
export const NO_FILTER: OfferFilter = {
  kind: "all",
  search: "",
  status: "any",
  source: "any",
};

const KIND_TABS: OfferKindTab[] = ["all", "promotion", "order", "booking"];
const SOURCES: OfferSource[] = ["own", "chain", "nenda", "pms"];
const STATUSES: OfferStatus[] = ["live", "scheduled", "ended", "off"];

/** The sources a row answers to. A PMS row is also the location's own. */
export function offerSources(offer: ResolvedRoomOffer): OfferSource[] {
  const out: OfferSource[] = [];
  if (!offer.inherited) out.push("own");
  else out.push(offer.origin === "global" ? "nenda" : "chain");
  if (hasImportedLines(offer)) out.push("pms");
  return out;
}

/** Does a row pass every part of the filter except, optionally, its kind? */
export function matchesOfferFilter(
  offer: ResolvedRoomOffer,
  status: OfferStatus,
  filter: OfferFilter,
  defaultLocale: string,
  ignoreKind = false
): boolean {
  if (!ignoreKind && filter.kind !== "all" && offer.action.kind !== filter.kind)
    return false;
  if (filter.status !== "any" && status !== filter.status) return false;
  if (filter.source !== "any" && !offerSources(offer).includes(filter.source))
    return false;
  const q = filter.search.trim().toLowerCase();
  if (q) {
    const title = (
      readLocalized(offer.title, defaultLocale) || ""
    ).toLowerCase();
    if (!title.includes(q)) return false;
  }
  return true;
}

const STATUS_COLOUR: Record<OfferStatus, string> = {
  live: tokens.success,
  scheduled: tokens.brand,
  ended: tokens.inkFaint,
  off: tokens.inkFaint,
};

export function OffersPage({
  offers,
  scope,
  loading,
  canEdit,
  preview,
  pmsImport,
  chainName,
  chainLocationCount,
  chainReach,
  defaultLocale,
  timezone,
  onCreate,
  onEdit,
  onDelete,
  onToggle,
  onOverride,
  onSuppress,
  notice,
  onDismissNotice,
}: OffersPageProps) {
  const { t } = useTranslation(["offers", "common"]);
  // Status is a function of the clock, so the list has to be told the clock
  // moved. Without this a row scheduled for 18:00 keeps saying "Scheduled" on
  // a CMS somebody left open all afternoon.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), STATUS_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const [filter, setFilter] = useState<OfferFilter>(NO_FILTER);
  const patch = (p: Partial<OfferFilter>) => setFilter((f) => ({ ...f, ...p }));

  // A row this location has hidden is OFF here whatever its window says.
  // Deriving the status from the schedule alone would print "Live now" beside
  // a switch that is off, and an operator will believe the column over the
  // switch.
  const rows = useMemo(
    () =>
      offers.map((offer) => ({
        offer,
        statusReason: offerStatusReason(offer, now, timezone),
        status: (offer.suppressed
          ? "off"
          : offerStatusAt(offer, now, timezone)) as OfferStatus,
      })),
    [offers, now, timezone]
  );
  // Tab counts follow the other filters, so a search shows where its matches are.
  const unkinded = rows.filter((r) =>
    matchesOfferFilter(r.offer, r.status, filter, defaultLocale, true)
  );
  const kindCount = (k: OfferKindTab) =>
    k === "all"
      ? unkinded.length
      : unkinded.filter((r) => r.offer.action.kind === k).length;
  const shown = unkinded.filter(
    (r) => filter.kind === "all" || r.offer.action.kind === filter.kind
  );
  // Only the sources this list actually has: a hotel with no chain and no PMS
  // has nothing to pick between.
  const sources = SOURCES.filter((src) =>
    offers.some((o) => offerSources(o).includes(src))
  );
  const filtered =
    filter.search.trim() !== "" ||
    filter.status !== "any" ||
    filter.source !== "any" ||
    filter.kind !== "all";
  const showKind = filter.kind === "all";

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("offersPage.eyebrow")}
        title={t("offersPage.title")}
        subtitle={t("offersPage.subtitle")}
        actions={
          canEdit ? (
            <Button
              variant="contained"
              size="small"
              endIcon={<AddIcon />}
              onClick={onCreate}
              data-testid="offers-new"
            >
              {t("offersPage.newOffer")}
            </Button>
          ) : undefined
        }
      />

      {/* POS-8, §6: wherever the demo payment provider is active, say so. It
          renders nothing when the money is real. */}
      <DemoPaymentsBanner />

      {chainName && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2,
            py: 1.25,
            mb: 2,
            borderRadius: "8px",
            border: `1px solid ${tokens.brand}`,
            bgcolor: tokens.brandTintSoft,
          }}
        >
          <Typography variant="body2" sx={{ color: tokens.ink }}>
            <Trans
              t={t}
              i18nKey="offersPage.chainBanner"
              values={{
                chain: chainName,
                reach: blastRadius(t, chainReach, chainLocationCount),
                detail:
                  chainReach && chainReach.premises.length > 0
                    ? `: ${chainReach.premises
                        .map((p) => `${p.premiseName} (${p.rooms})`)
                        .join(", ")}`
                    : "",
              }}
              components={{
                strong: <strong />,
                reach: <strong data-testid="offers-reach" />,
              }}
            />
          </Typography>
        </Box>
      )}

      {notice && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={onDismissNotice}
          data-testid="offers-notice"
        >
          {notice}
        </Alert>
      )}

      {pmsImport}

      {preview && <Box sx={{ mb: 2 }}>{preview}</Box>}

      {offers.length > 0 && (
        <Box data-testid="offers-selector" sx={{ mb: 1.5 }}>
          <Tabs
            value={filter.kind}
            onChange={(_, v: OfferKindTab) => patch({ kind: v })}
            sx={{ mb: 1.5, borderBottom: `1px solid ${tokens.line}` }}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            {KIND_TABS.map((tab) => (
              <Tab
                key={tab}
                value={tab}
                label={t("offersPage.tabs.withCount", {
                  label: t(`offersPage.tabs.${tab}`),
                  count: kindCount(tab),
                })}
                data-testid={`offers-tab-${tab}`}
              />
            ))}
          </Tabs>
          <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
            <TextField
              size="small"
              placeholder={t("offersPage.filters.search")}
              value={filter.search}
              onChange={(e) => patch({ search: e.target.value })}
              sx={{ minWidth: 220, flex: "1 1 220px", maxWidth: 360 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ "data-testid": "offers-search" }}
            />
            <TextField
              select
              size="small"
              label={t("offersPage.filters.status")}
              value={filter.status}
              onChange={(e) =>
                patch({ status: e.target.value as OfferFilter["status"] })
              }
              sx={{ minWidth: 150 }}
              data-testid="offers-status"
            >
              <MenuItem value="any">
                {t("offersPage.filters.anyStatus")}
              </MenuItem>
              {STATUSES.map((st) => (
                <MenuItem key={st} value={st}>
                  {offerStatusLabel(t, st)}
                </MenuItem>
              ))}
            </TextField>
            {sources.length > 1 && (
              <TextField
                select
                size="small"
                label={t("offersPage.filters.source")}
                value={filter.source}
                onChange={(e) =>
                  patch({ source: e.target.value as OfferFilter["source"] })
                }
                sx={{ minWidth: 170 }}
                data-testid="offers-source"
              >
                <MenuItem value="any">
                  {t("offersPage.filters.anySource")}
                </MenuItem>
                {sources.map((src) => (
                  <MenuItem key={src} value={src}>
                    {t(`offersPage.sources.${src}`)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          border: `1px solid ${tokens.divider}`,
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: tokens.surface,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("offersPage.columns.offer")}</TableCell>
              {showKind && (
                <TableCell>{t("offersPage.columns.kind")}</TableCell>
              )}
              <TableCell>{t("offersPage.columns.whatTheGuestGets")}</TableCell>
              <TableCell>{t("offersPage.columns.picks")}</TableCell>
              <TableCell>{t("offersPage.columns.status")}</TableCell>
              <TableCell width={64} align="center">
                {t("offersPage.columns.on")}
              </TableCell>
              <TableCell width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map(({ offer, status, statusReason }) => {
              const own = !offer.inherited;
              const editable = canEdit && own;
              // Overriding is a PREMISE act. On a chain page the rows are the
              // chain's own plus Nenda's, and re-deciding one of those "for
              // everyone" is simply editing it.
              const overridable =
                canEdit && offer.inherited && scope.kind === "premise";
              return (
                <TableRow
                  key={offer.id}
                  hover
                  data-testid={`offer-row-${offer.id}`}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1.25}>
                      <Box sx={{ color: tokens.inkMuted, flex: "none" }}>
                        <OfferIcon
                          name={offer.items[0]?.icon ?? "generic"}
                          size={20}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "fontWeightBold",
                            color: tokens.ink,
                          }}
                        >
                          {readLocalized(offer.title, defaultLocale) ||
                            t("offersPage.untitled")}
                        </Typography>
                        {(offer.featured || hasImportedLines(offer)) && (
                          <Stack
                            direction="row"
                            gap={0.5}
                            flexWrap="wrap"
                            sx={{ mt: 0.25 }}
                          >
                            {offer.featured && (
                              <Chip
                                size="small"
                                label={t("offersPage.featured")}
                                data-testid={`offer-featured-${offer.id}`}
                                sx={{
                                  height: 18,
                                  typography: "micro",
                                  bgcolor: tokens.brandTintSoft,
                                  color: tokens.brand,
                                }}
                              />
                            )}
                            {/* Said on the row, because an operator editing
                                an imported price should know a PMS feeds it -
                                and that their edit will be kept. */}
                            {hasImportedLines(offer) && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t("offersPage.fromYourPms")}
                                data-testid={`offer-imported-${offer.id}`}
                                sx={{ height: 18, typography: "micro" }}
                              />
                            )}
                          </Stack>
                        )}
                        {!own && (
                          <Stack
                            direction="row"
                            gap={0.5}
                            flexWrap="wrap"
                            sx={{ mt: 0.25 }}
                          >
                            {/* Where it came from, on every inherited row.
                                Without it a hotel cannot tell the spa evening
                                it wrote from the one its chain wrote, and the
                                first thing it does with the chain's is try to
                                edit it. */}
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t(ORIGIN_KEY[offer.origin])}
                              data-testid={`offer-origin-${offer.id}`}
                              sx={{ height: 18, typography: "micro" }}
                            />
                            {offer.overriddenFields.length > 0 && (
                              <Chip
                                size="small"
                                label={t("offersPage.overriddenHere", {
                                  fields: offer.overriddenFields
                                    .map((f) => t(OVERRIDE_FIELD_KEY[f]))
                                    .join(", "),
                                })}
                                data-testid={`offer-overridden-${offer.id}`}
                                sx={{
                                  height: 18,
                                  typography: "micro",
                                  bgcolor: tokens.brandTintSoft,
                                  color: tokens.brand,
                                }}
                              />
                            )}
                            {offer.suppressed && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t("offersPage.hiddenHere")}
                                data-testid={`offer-suppressed-${offer.id}`}
                                sx={{
                                  height: 18,
                                  typography: "micro",
                                  color: tokens.inkFaint,
                                }}
                              />
                            )}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  {showKind && (
                    <TableCell sx={{ color: tokens.inkMuted }}>
                      {offerKindLabel(t, offer.action.kind)}
                    </TableCell>
                  )}
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {kindSummary(t, offer)}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {offer.action.kind === "booking" &&
                    offer.action.slots.mode === "slots"
                      ? t("offersPage.generatedTimes")
                      : offer.items.length === 0
                        ? t("common:labels.emptyValue")
                        : `${offer.items.length}`}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      // The bare word is what gets reported as a bug: an offer
                      // that looks right, reading "Scheduled", with nothing
                      // saying which gate is holding it. Name the gate.
                      label={
                        status === "scheduled" && statusReason
                          ? t("offersPage.statusWithReason", {
                              status: offerStatusLabel(t, status),
                              reason: statusReason,
                            })
                          : offerStatusLabel(t, status)
                      }
                      data-testid={`offer-status-${offer.id}`}
                      sx={{
                        height: 22,
                        typography: "micro",
                        color: STATUS_COLOUR[status],
                        borderColor: STATUS_COLOUR[status],
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {/* Going live is a LIST control, never a side effect of
                        fixing a typo in the editor.

                        On an INHERITED row the same switch means something
                        narrower and says so: it hides the row HERE, and leaves
                        the chain's own switch alone (POS-14). Two controls —
                        one for "on" and one for "hidden at this location" —
                        would be two words for the one question an operator is
                        asking, which is whether guests in THIS building see it.
                        The switch is dead only when the chain itself has
                        switched the row off: suppression is one-way, and a
                        hotel must not put back what its chain withdrew. */}
                    <Tooltip
                      title={
                        editable
                          ? offer.enabled
                            ? t("offersPage.switch.off")
                            : t("offersPage.switch.on")
                          : overridable && onSuppress
                            ? offer.enabled
                              ? offer.suppressed
                                ? t("offersPage.switch.showAgain")
                                : t("offersPage.switch.hideHere")
                              : t("offersPage.switch.offAtSource")
                            : t("offersPage.switch.inherited")
                      }
                    >
                      <span>
                        <Switch
                          size="small"
                          checked={
                            offer.enabled &&
                            !(offer.inherited && offer.suppressed)
                          }
                          disabled={
                            editable
                              ? false
                              : !(overridable && onSuppress && offer.enabled)
                          }
                          onChange={(e) => {
                            if (editable)
                              return onToggle(offer, e.target.checked);
                            onSuppress?.(offer, !e.target.checked);
                          }}
                          inputProps={{
                            "aria-label": t("offersPage.showToGuests", {
                              name:
                                readLocalized(offer.title, defaultLocale) ||
                                t("offersPage.thisOffer"),
                            }),
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {editable && (
                      <RowMenu
                        onEdit={() => onEdit(offer)}
                        onDelete={() => onDelete(offer)}
                      />
                    )}
                    {!editable && overridable && onOverride && (
                      <InheritedRowMenu
                        overridden={
                          offer.overriddenFields.length > 0 || offer.suppressed
                        }
                        onOverride={() => onOverride(offer)}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {offers.length > 0 && shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={showKind ? 7 : 6}>
                  <Stack
                    alignItems="center"
                    sx={{ py: 4 }}
                    gap={1}
                    data-testid="offers-no-match"
                  >
                    <Typography
                      variant="bodySm"
                      sx={{ color: tokens.inkMuted }}
                    >
                      {t("offersPage.filters.noMatch")}
                    </Typography>
                    {filtered && (
                      <Button size="small" onClick={() => setFilter(NO_FILTER)}>
                        {t("offersPage.filters.clear")}
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {offers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Stack alignItems="center" sx={{ py: 5 }} gap={0.5}>
                    {loading ? (
                      <Typography
                        variant="bodySm"
                        sx={{ color: tokens.inkMuted }}
                      >
                        {t("common:states.loading")}
                      </Typography>
                    ) : (
                      <>
                        <Typography
                          sx={{
                            fontWeight: "fontWeightSemibold",
                            color: tokens.ink,
                          }}
                        >
                          {t("offersPage.emptyTitle")}
                        </Typography>
                        <Typography
                          variant="bodySm"
                          sx={{
                            color: tokens.inkMuted,
                            textAlign: "center",
                            maxWidth: 460,
                          }}
                        >
                          {t("offersPage.emptyHelp")}
                        </Typography>
                      </>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </PageContainer>
  );
}

function RowMenu({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation(["offers", "common"]);
  const [el, setEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setEl(e.currentTarget)}
        aria-label={t("offersPage.rowMenu")}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={el}
        open={Boolean(el)}
        onClose={() => setEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setEl(null);
            onEdit();
          }}
        >
          <EditOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
          {t("common:buttons.edit")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setEl(null);
            onDelete();
          }}
          sx={{ color: tokens.error }}
        >
          <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
          {t("common:buttons.delete")}
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * The row menu an INHERITED row gets (`POS-14`).
 *
 * A separate control from `RowMenu`, with its own label, because it offers a
 * different verb: Edit and Delete would both 404 here — the row belongs to a
 * layer this page cannot write — while "Override for this location" always
 * works. A menu whose items sometimes fail is worse than two menus.
 */
function InheritedRowMenu({
  overridden,
  onOverride,
}: {
  overridden: boolean;
  onOverride: () => void;
}) {
  const { t } = useTranslation("offers");
  const [el, setEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setEl(e.currentTarget)}
        aria-label={t("offersPage.inheritedRowMenu")}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={el}
        open={Boolean(el)}
        onClose={() => setEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setEl(null);
            onOverride();
          }}
        >
          <TuneIcon fontSize="small" style={{ marginRight: 8 }} />
          {overridden ? t("offersPage.editOverride") : t("offersPage.override")}
        </MenuItem>
      </Menu>
    </>
  );
}

/* -------------------------------------------------------------------------- */

interface ContainerProps {
  /** Exactly one of the two. A location, or the whole customer. */
  premise?: PremiseConfig;
  companyId?: string;
  companyName?: string;
  companyLocationCount?: number;
}

/**
 * Two operator languages, when there is no premise to ask.
 *
 * A customer is not a place and has no `supportedLocales`; a chain-level offer
 * is written once for hotels that may each declare a different list. Swedish
 * and English are the two Foyer is actually operated in, and a hotel editing
 * the same row at its own layer still sees its own list — so the cost of the
 * guess is an extra empty input, not a lost translation.
 */
const CHAIN_LOCALES = ["sv", "en"];

/**
 * Offers, wired.
 *
 * Both layers arrive in one call — the catalogue route already answers with a
 * location's own rows plus the ones it inherits — so there is no second round
 * trip and no way for the two halves to be out of step with each other.
 */
export default function OffersContainer({
  premise,
  companyId,
  companyName,
  companyLocationCount,
}: ContainerProps) {
  const toast = useToast();
  const { t } = useTranslation(["offers", "common"]);
  const offerName = (o: { title: RoomOffer["title"] }) =>
    readLocalized(o.title, premise?.defaultLocale ?? CHAIN_LOCALES[0]) ||
    t("offersPage.theOffer");
  const scope: OfferScopeRef = useMemo(
    () =>
      premise
        ? { kind: "premise", premiseId: premise.premiseId }
        : { kind: "company", companyId: companyId ?? "" },
    [premise, companyId]
  );
  const place: PlaceRef =
    scope.kind === "premise"
      ? { premiseId: scope.premiseId }
      : { companyId: scope.companyId };
  const mayEdit = usePermission("offers", "edit", place);
  const mayReadReach = usePermission("offers", "view", place);
  const canEdit = mayEdit;
  const scopeKey = offerScopeKey(scope);

  const [offers, setOffers] = useState<RoomOffer[]>([]);
  const [overrides, setOverrides] = useState<RoomOfferOverride[]>([]);
  const [reach, setReach] = useState<OfferReach | undefined>(undefined);
  /** `undefined` = closed. The inherited row being re-decided for this place. */
  const [overriding, setOverriding] = useState<ResolvedRoomOffer | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  /** `undefined` = closed, `null` = creating, a row = editing it. */
  const [editing, setEditing] = useState<RoomOffer | null | undefined>(
    undefined
  );
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState<OfferProblem[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** A refusal from a LIST control, where no dialog is open to show it in. */
  const [notice, setNotice] = useState<string | null>(null);
  /** Bumped after every write, so the preview iframe refetches the feed. */
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => {
    let alive = true;
    setLoading(true);
    // The catalogue and this location's deltas, together. In PARALLEL and
    // resolved as one list: fetching the overrides after the rows would draw
    // the chain's price for a moment and then replace it with the hotel's,
    // which reads as a bug in the price rather than as a load.
    Promise.all([
      foyerApi.listRoomOffers(scope),
      scope.kind === "premise"
        ? // Caught on its OWN leg. A blip on the deltas must not blank a
          // catalogue: no overrides means the chain's own prices, which is
          // wrong in one field, while an empty list reads as "this hotel
          // offers nothing" and sends somebody to author it all again.
          foyerApi
            .listOfferOverrides(scope.premiseId)
            .catch(() => [] as RoomOfferOverride[])
        : Promise.resolve<RoomOfferOverride[]>([]),
    ])
      .then(([list, deltas]) => {
        if (!alive) return;
        setOffers(list);
        setOverrides(deltas);
      })
      .catch(() => {
        if (!alive) return;
        setOffers([]);
        setOverrides([]);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey]);

  useEffect(() => reload(), [reload]);

  /*
   * The blast radius, fetched once per customer.
   *
   * Its own call rather than a field on the catalogue: the banner has to be
   * right before the FIRST save, including on a chain whose catalogue is still
   * empty, and a count folded into a list response would arrive only once there
   * was a list.
   */
  useEffect(() => {
    // Cleared FIRST, on every customer change. This container is not remounted
    // when the scope picker moves, so a stale reach would put chain A's
    // location and room counts under chain B's name until the new call landed
    // — which is exactly the wrong-blast-radius mistake the banner exists to
    // prevent.
    setReach(undefined);
    if (scope.kind !== "company" || !scope.companyId || !mayReadReach) return;
    let alive = true;
    foyerApi
      .offerReach(scope.companyId)
      .then((r) => alive && setReach(r))
      // Left undefined rather than zeroed: the banner then falls back to the
      // directory's location count and says nothing about rooms. A count of 0
      // rooms would read as a promise that this reaches nobody.
      .catch(() => alive && setReach(undefined));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey, mayReadReach]);

  /**
   * The list this page draws — the ONE resolver, the one the television runs.
   *
   * `includeSuppressed` is true here and nowhere else: a row this location has
   * hidden still has to be reachable to be un-hidden. At company scope there is
   * no premise doing the reading, so the rows are tagged by which layer this
   * page can WRITE — global rows arrive read-only there for the same reason a
   * chain row does at a hotel.
   */
  const rows: ResolvedRoomOffer[] = useMemo(() => {
    if (scope.kind === "premise") {
      return resolveRoomOffers({
        offers,
        overrides,
        premiseId: scope.premiseId,
        includeSuppressed: true,
      });
    }
    return offers.map((offer) => ({
      ...offer,
      origin: offer.scope,
      inherited: !isOwnRow(offer, scope),
      suppressed: false,
      overriddenFields: [],
    }));
  }, [offers, overrides, scope]);

  /**
   * Resolves `true` on success. The dialog then slides itself out and its
   * `onClose` clears `editing`: closing it from here would unmount the drawer
   * mid-slide.
   */
  const save = async (body: RoomOfferBody): Promise<boolean> => {
    setSaving(true);
    setProblems([]);
    setError(null);
    try {
      const saved = editing
        ? await foyerApi.updateRoomOffer(scope, editing.id, body)
        : await foyerApi.createRoomOffer(scope, body);
      // Adopt the server's own row rather than the draft: it stamps ids on new
      // items, re-numbers `order` and normalises the localised strings, and a
      // list still showing the draft would disagree with the next reload.
      setOffers((prev) =>
        prev.some((o) => o.id === saved.id)
          ? prev.map((o) => (o.id === saved.id ? saved : o))
          : [...prev, saved]
      );
      setTick((t) => t + 1);
      toast.success(
        editing
          ? t("offersPage.updated", { name: offerName(saved) })
          : t("offersPage.created", { name: offerName(saved) })
      );
      return true;
    } catch (e) {
      const refused = offerProblemsOf(t, e);
      if (refused) setProblems(refused);
      else setError((e as Error).message || t("offersPage.couldNotSave"));
      toast.error(
        refused ? t("offersPage.fixFields") : describeError(t, "saveOffer", e)
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  /*
   * Both optimistic writes below roll back ONE ROW, never the whole list.
   *
   * Restoring a captured snapshot looks equivalent and is not: two switches
   * flipped in quick succession share a stale closure, so the first one's
   * failure would silently undo the second one's success. A functional update
   * keyed on the row's own id cannot reach a row nobody touched.
   */
  const toggle = async (offer: RoomOffer, enabled: boolean) => {
    setOffers((prev) =>
      prev.map((o) => (o.id === offer.id ? { ...o, enabled } : o))
    );
    try {
      const saved = await foyerApi.updateRoomOffer(scope, offer.id, {
        enabled,
      });
      setOffers((prev) => prev.map((o) => (o.id === saved.id ? saved : o)));
      setTick((t) => t + 1);
      toast.success(
        enabled
          ? t("offersPage.shown", { name: offerName(offer) })
          : t("offersPage.hidden", { name: offerName(offer) })
      );
    } catch (e) {
      toast.error(
        describeError(t, enabled ? "showOffer" : "hideOffer", e, {
          name: offerName(offer),
        })
      );
      // Put it back rather than leave the switch lying about what guests see.
      setOffers((prev) =>
        prev.map((o) =>
          o.id === offer.id ? { ...o, enabled: offer.enabled } : o
        )
      );
    }
  };

  const remove = async (offer: RoomOffer) => {
    // Where it sat, so a failed delete does not also reshuffle the list. Read
    // inside the updater rather than from the render closure, for the same
    // reason the toggle above uses one.
    let index = -1;
    setOffers((prev) => {
      const found = prev.findIndex((o) => o.id === offer.id);
      if (found >= 0) index = found;
      return prev.filter((o) => o.id !== offer.id);
    });
    try {
      await foyerApi.deleteRoomOffer(scope, offer.id);
      setTick((t) => t + 1);
      toast.success(t("offersPage.deleted", { name: offerName(offer) }));
    } catch (e) {
      toast.error(
        describeError(t, "deleteOffer", e, { name: offerName(offer) })
      );
      setOffers((prev) => {
        const next = [...prev];
        next.splice(index < 0 ? next.length : index, 0, offer);
        return next;
      });
    }
  };

  /* -- POS-14: this location's decisions about inherited rows ----------- */

  /**
   * Write one delta and adopt the server's own row.
   *
   * `patch` REPLACES whatever this location held before, so the dialog always
   * sends the whole of it — see `putOfferOverride`. An empty patch with
   * `suppressed: false` is a legitimate save meaning "no differences here", and
   * it is not turned into a delete: an operator who cleared every field can
   * still see the row is tracked, and reverting is its own button.
   */
  const saveOverride = async (
    offer: ResolvedRoomOffer,
    body: { suppressed?: boolean; patch?: OfferOverridePatch }
  ): Promise<boolean> => {
    if (scope.kind !== "premise") return false;
    setSaving(true);
    setProblems([]);
    setError(null);
    try {
      const saved = await foyerApi.putOfferOverride(
        scope.premiseId,
        offer.id,
        body
      );
      setOverrides((prev) => [
        ...prev.filter((o) => o.offerId !== saved.offerId),
        saved,
      ]);
      setTick((t) => t + 1);
      toast.success(t("offersPage.overrideSaved", { name: offerName(offer) }));
      return true;
    } catch (e) {
      const refused = offerProblemsOf(t, e);
      if (refused) setProblems(refused);
      else
        setError((e as Error).message || t("offersPage.couldNotSaveOverride"));
      toast.error(
        refused
          ? t("offersPage.overrideFixFields", { name: offerName(offer) })
          : describeError(t, "saveOfferChanges", e, {
              name: offerName(offer),
            })
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const revertOverride = async (offer: ResolvedRoomOffer): Promise<boolean> => {
    if (scope.kind !== "premise") return false;
    setSaving(true);
    try {
      await foyerApi.deleteOfferOverride(scope.premiseId, offer.id);
      setOverrides((prev) => prev.filter((o) => o.offerId !== offer.id));
      setTick((t) => t + 1);
      toast.success(t("offersPage.reverted", { name: offerName(offer) }));
      return true;
    } catch (e) {
      setError((e as Error).message || t("offersPage.couldNotRevert"));
      toast.error(
        describeError(t, "revertOffer", e, { name: offerName(offer) })
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Hide, or show again, an inherited row at this location.
   *
   * Un-hiding a row whose delta says nothing else DELETES the delta rather than
   * storing `suppressed: false` — a record that re-decides nothing is a record
   * that would make the row read "Overridden here" forever. A delta that still
   * carries a price keeps it.
   */
  const suppress = async (offer: ResolvedRoomOffer, suppressed: boolean) => {
    if (scope.kind !== "premise") return;
    const patch = overrides.find((o) => o.offerId === offer.id)?.patch ?? {};
    const dropIt = !suppressed && Object.keys(patch).length === 0;
    setNotice(null);
    setSaving(true);
    try {
      if (dropIt) {
        await foyerApi.deleteOfferOverride(scope.premiseId, offer.id);
        setOverrides((prev) => prev.filter((o) => o.offerId !== offer.id));
      } else {
        const saved = await foyerApi.putOfferOverride(
          scope.premiseId,
          offer.id,
          { suppressed, patch }
        );
        setOverrides((prev) => [
          ...prev.filter((o) => o.offerId !== saved.offerId),
          saved,
        ]);
      }
      setTick((t) => t + 1);
      toast.success(
        suppressed
          ? t("offersPage.hiddenHereToast", { name: offerName(offer) })
          : t("offersPage.shownAgainToast", { name: offerName(offer) })
      );
    } catch (e) {
      toast.error(
        describeError(
          t,
          suppressed ? "hideOfferAtLocation" : "showOfferAgainAtLocation",
          e,
          { name: offerName(offer) }
        )
      );
      // SAID OUT LOUD, not just rolled back. The switch springing back with no
      // message is how somebody walks away believing a chain campaign is hidden
      // at their hotel while it is still on every screen in the building.
      setNotice(
        suppressed
          ? t("offersPage.couldNotHide")
          : t("offersPage.couldNotShowAgain")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <OffersPage
        offers={rows}
        scope={scope}
        loading={loading}
        canEdit={canEdit}
        defaultLocale={premise?.defaultLocale ?? CHAIN_LOCALES[0]}
        // A chain has no one clock. Left undefined there, so the status column
        // falls back to UTC rather than pretending one location's zone speaks
        // for four - and every chain row is inherited-read at a location, where
        // it IS resolved in that location's own zone.
        timezone={premise?.address?.timezone}
        chainName={premise ? undefined : companyName}
        chainLocationCount={companyLocationCount}
        chainReach={reach}
        pmsImport={
          premise ? (
            <PmsOffersImport
              premiseId={premise.premiseId}
              canEdit={canEdit}
              onSynced={() => {
                reload();
                setTick((t) => t + 1);
              }}
            />
          ) : undefined
        }
        preview={
          premise ? (
            // Remounted on every write so the embedded TV refetches the feed.
            // Offers live in their own collection, not in `PremiseConfig`, so
            // the postMessage channel the preview normally rides has nothing
            // to push — a remount is the only honest refresh.
            <LivePreview key={tick} premise={premise} screen="offers" />
          ) : undefined
        }
        onCreate={() => {
          setProblems([]);
          setError(null);
          setEditing(null);
        }}
        onEdit={(offer) => {
          setProblems([]);
          setError(null);
          setEditing(offer);
        }}
        onDelete={remove}
        onToggle={toggle}
        onOverride={(offer) => {
          setProblems([]);
          setError(null);
          setOverriding(offer);
        }}
        onSuppress={suppress}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
      />
      {editing !== undefined && (
        // Keyed by the record: opening a different one REMOUNTS the dialog, and
        // the remount is the reset. See OfferDialog's docblock.
        <OfferDialog
          key={editing?.id ?? "new"}
          open
          initial={editing}
          supportedLocales={premise?.supportedLocales ?? CHAIN_LOCALES}
          defaultLocale={premise?.defaultLocale ?? CHAIN_LOCALES[0]}
          saving={saving}
          serverProblems={problems}
          error={error}
          onClose={() => setEditing(undefined)}
          onSave={save}
        />
      )}
      {overriding && (
        // `original` is the RAW row off the catalogue, not the resolved one:
        // the dialog has to say what each field would go back to, and the
        // resolved row is already wearing this location's answers.
        <OfferOverrideDialog
          key={overriding.id}
          offer={overriding}
          original={offers.find((o) => o.id === overriding.id) ?? overriding}
          saving={saving}
          serverProblems={problems}
          error={error}
          onClose={() => setOverriding(undefined)}
          onSave={(patch) =>
            saveOverride(overriding, {
              suppressed: overriding.suppressed,
              patch,
            })
          }
          onRevert={
            overriding.overrideId ? () => revertOverride(overriding) : undefined
          }
        />
      )}
    </>
  );
}
